import { zodResolver } from "@hookform/resolvers/zod";
import { ToggleGroup } from "@radix-ui/react-toggle-group";
import { TFunction } from "i18next";
import { CircleCheck, ExternalLink } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Button } from "../../components/ui/button/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card/card";
import { Combobox } from "../../components/ui/combo-box/combo-box";
import { FailureMTUList } from "../../components/ui/failure-mtu-list/failure-mtu-list";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form/form";
import { Skeleton } from "../../components/ui/skeleton/skeleton";
import { Textarea } from "../../components/ui/textarea/textarea";
import { ToggleGroupItem } from "../../components/ui/toggle-group/toggle-group";
import { cn, createOpcenterMduiCtx } from "../../lib/utils";
import { useNC } from "../../queries/nc/use-nc";
import { useNCFailures } from "../../queries/nc/use-nc-failures";
import { useNCMTUs } from "../../queries/nc/use-nc-mtus";
import { useNCReworkCodes } from "../../queries/nc/use-nc-rework-codes";
import { useNCTransitions } from "../../queries/nc/use-nc-transitions";
import { useSentenceNC } from "../../queries/nc/use-sentence-nc";
import { QueryError } from "@/components/ui/query-error/query-error";
import { ErrorPopover } from "@/components/ui/error-popover/error-popover";
import { ROUTES } from "../../routes";
import { opcenter } from "../../../config.json";

const schema = (t: TFunction<"translation">, initialStatus?: string) =>
  z
    .object({
      status: z
        .string()
        .min(1, t("nc.sentence.status.error"))
        .refine((s) => s !== initialStatus),
      reworkCode: z.string().optional(),
      note: z.string().max(255),
    })
    .refine((data) => !(data.status === "REWORK" && data.reworkCode === ""), {
      path: ["reworkCode"],
      message: t("nc.sentence.reworkCode.error"),
    });

type FormValues = z.infer<ReturnType<typeof schema>>;

const useSentenceNCForm = (initialStatus?: string) => {
  const { t } = useTranslation();
  return useForm<FormValues>({
    resolver: zodResolver(schema(t, initialStatus)),
    defaultValues: {
      status: "",
      reworkCode: "",
      note: "",
    },
  });
};

export function SentenceNC({
  ncNId,
  onCancel,
  onSuccess,
}: {
  ncNId: string;
  onSuccess?: VoidFunction;
  onCancel?: VoidFunction;
}) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const { nc, error: ncError, refetch: refetchNc } = useNC(ncNId);
  const {
    ncFailures,
    isPending: ncFailuresPending,
    error: ncFailuresError,
    refetch: refetchNcFailures,
  } = useNCFailures(nc?.Id);
  const {
    ncMTUs,
    isPending: ncMTUsPending,
    error: ncMTUsError,
    refetch: refetchNcMTUs,
  } = useNCMTUs(nc?.Id);
  const {
    transitions,
    isPending: statusesPending,
    error: transitionsError,
    refetch: refetchTransitions,
  } = useNCTransitions(nc?.Status ?? undefined);
  const { reworkCodes } = useNCReworkCodes(
    nc?.Defects?.map((x) => x.FailureNId ?? "") ?? []
  );
  const form = useSentenceNCForm(nc?.Status ?? undefined);
  const {
    sentenceNC,
    isPending,
    error: sentenceNcError,
  } = useSentenceNC({ onSuccess });

  const statuses = [
    nc?.Status ?? "",
    ...(transitions?.map((x) => x.ToStatus?.Name ?? "") ?? []),
  ];

  const statusValue = form.watch("status");

  const mduiCtx = useMemo(() => {
    if (!nc) return;
    return createOpcenterMduiCtx({
      stateParams: {
        Id: nc.Id,
        isDrillDownState: true,
      },
      tracking: [
        {
          index: 0,
          master: { id: nc.Id },
          screenId: opcenter.nc,
          detail: {},
          params: {},
        },
      ],
    });
  }, [nc]);

  const handleSentence = useCallback(
    (values: FormValues) =>
      sentenceNC({
        ncNId: ncNId,
        statusNId: values.status,
        note: values.note,
        reworkId: values.reworkCode,
      }),
    [ncNId, sentenceNC]
  );

  useEffect(() => {
    if (!nc?.Status) return;

    form.setValue("status", nc?.Status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nc?.Status]);

  if (ncError || ncFailuresError || ncMTUsError || transitionsError) {
    return (
      <div className="p-5">
        <QueryError
          errors={[ncError, ncFailuresError, ncMTUsError, transitionsError]}
          retries={[
            refetchNc,
            refetchNcFailures,
            refetchNcMTUs,
            refetchTransitions,
          ]}
        />
      </div>
    );
  }

  return (
    <>
      <div ref={setContainer} className="flex flex-col gap-5 overflow-auto p-5">
        <Link
          to={
            ROUTES.OPCENTER(opcenter.nc, t("opcenter.title")) +
            `&mduiCtx=${mduiCtx}`
          }
        >
          <Card className="bg-success-muted text-success flex flex-col items-center gap-2.5 p-5 rounded-2xl border-success">
            <CircleCheck className="h-8 w-8" />
            <p className="text-base text-center font-bold underline">{ncNId}</p>
            <ExternalLink />
          </Card>
        </Link>
        <FailureMTUList
          failuresPending={ncFailuresPending}
          mtusPending={ncMTUsPending}
          mtus={ncMTUs?.map((x) => ({ code: x.Code ?? "", id: x.Id ?? "" }))}
          failures={ncFailures?.map((x) => ({
            code: x.NId ?? "",
            id: x.Id ?? "",
            name: x.Name ?? "",
          }))}
        />
        <Form {...form}>
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-semibold">
                  {t("nc.sentence.status.label")}*
                </FormLabel>
                <FormControl>
                  <ToggleGroup
                    type="single"
                    value={field.value}
                    onValueChange={(e) => {
                      field.onChange(e);
                      form.resetField("reworkCode");
                    }}
                    className="grid grid-cols-3 gap-4"
                  >
                    {statuses?.map((x) => (
                      <ToggleGroupItem value={x} key={x} className="capitalize">
                        {x.toLowerCase()}
                      </ToggleGroupItem>
                    ))}
                    {statusesPending &&
                      [...Array(4)].map((_, i) => (
                        <Skeleton className="h-[42px] w-full" key={i} />
                      ))}
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {statusValue === "REWORK" && (
            <FormField
              control={form.control}
              name="reworkCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">
                    {t("nc.sentence.reworkCode.label")}*
                  </FormLabel>
                  <FormControl>
                    <Combobox
                      value={field.value ?? ""}
                      container={container}
                      items={
                        reworkCodes?.map((x) => ({
                          label: `${x.FailureNId} ${x.ProcessId?.Name}`,
                          description: x.ProcessId?.Description,
                          processName: x.ProcessId?.Name,
                          value: x.Id,
                          failureNId: x.FailureNId,
                        })) ?? []
                      }
                      title={t("nc.sentence.reworkCode.label")}
                      onSelect={field.onChange}
                      popOverClassName=" max-h-[400px] pointer-event-auto "
                      renderGroup={(children) => (
                        <div className="grid grid-cols-2 gap-3 p-3 pointer-event-auto">
                          {children}
                        </div>
                      )}
                      renderItem={(item, selected) => (
                        <Card
                          className={cn(
                            selected ? "bg-primary text-white" : "bg-inherit",
                            "border pointer-event-auto",
                            "hover:bg-primary hover:text-white flex items-center gap-2 p-2 h-full"
                          )}
                        >
                          <p className="font-bold min-w-7 text-center">
                            {item.failureNId}
                          </p>
                          <div>
                            <CardHeader className="text-sm p-0 pb-1">
                              <CardTitle>{item.processName}</CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm p-0">
                              <p>{item.description}</p>
                            </CardContent>
                          </div>
                        </Card>
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {statusValue !== nc?.Status && statusValue !== "" && (
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">
                    {t("nc.details.note.label")}
                  </FormLabel>
                  <FormControl className="grid ">
                    <Textarea
                      placeholder={t("nc.details.note.placeholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </Form>
      </div>
      <div className="mt-auto grid grid-cols-2 gap-2.5 pb-5 px-5">
        <Button size="default" variant="secondary" onClick={onCancel}>
          {t("nc.sentence.close")}
        </Button>
        <ErrorPopover error={sentenceNcError}>
          <Button
            size="default"
            disabled={!form.formState.isValid || isPending}
            onClick={form.handleSubmit(handleSentence)}
            loading={isPending}
          >
            {t("nc.sentence.save")}
          </Button>
        </ErrorPopover>
      </div>
    </>
  );
}
